import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { roundAtSecondDecimal } from './numbers';
import { isNilOrEmpty } from './objectUtils';

export interface ObjectWithId {
  id: string;
}

export const isIdInList = <T extends ObjectWithId>(value: T, list: T[]) => {
  return list.some((v) => v.id === value.id);
};

export const findById = <T extends ObjectWithId>(list: T[], id: string): T | undefined =>
  list.find((object) => object.id === id);

export const uniquePushId = <T extends ObjectWithId>(listToMutate: T[], objectToAdd: T) => {
  if (!isIdInList(objectToAdd, listToMutate)) listToMutate.push(objectToAdd);
};

export const resolveCompleteItemsById = <T extends ObjectWithId>(references: T[], completeItems: T[]): T[] =>
  references.reduce((acc: T[], reference) => {
    const completeItem = findById(completeItems, reference.id);
    if (!isNil(completeItem)) {
      acc.push(completeItem);
    }
    return acc;
  }, []);

export const uniqueObjectWithIdAppend = <T extends ObjectWithId>(list1: T[], list2: T[]) => {
  const itemsToAdd = list2.reduce((acc: T[], itemToAppend: T) => {
    if (!isIdInList(itemToAppend, acc) && !isIdInList(itemToAppend, list1)) {
      acc.push(itemToAppend);
    }
    return acc;
  }, []);
  return [...list1, ...itemsToAdd];
};

export const isObjectInList = <T>(value: T, list: T[]) => {
  return !isNil(list.find((v) => isEqual(value, v)));
};

export const uniquePush = <T>(listToMutate: T[], objectToAdd: T) => {
  if (!isObjectInList(objectToAdd, listToMutate)) listToMutate.push(objectToAdd);
};

export const uniquePushAndReturnList = <T>(list: T[], objectToAdd: T) => {
  const finalList = cloneDeep(list);
  if (!isObjectInList(objectToAdd, finalList)) finalList.push(objectToAdd);
  return finalList;
};

export const hasDuplicate = <T>(listToCheck: T[], key: keyof T): boolean => {
  return !isEmpty(
    listToCheck.filter((item, index) => listToCheck.findIndex((item2) => item2[key] === item[key]) != index)
  );
};

export const mergeDuplicates = <T>(listToMerge: T[], key: keyof T, mergeFunction: (item1: T, item2: T) => T): T[] => {
  return listToMerge.reduce((acc: T[], item: T) => {
    const alreadyExistingItemIndex = acc.findIndex((ai) => ai[key] === item[key]);
    if (alreadyExistingItemIndex === -1) acc.push(item);
    else acc[alreadyExistingItemIndex] = mergeFunction(acc[alreadyExistingItemIndex], item);
    return acc;
  }, []);
};

export const arraySubsetOfOtherArray = <T>(array: T[], subsetArray: T[]) => {
  return !subsetArray.some((val) => array.indexOf(val) === -1);
};

/**
 * Filter the array in argument based on the condition and returns the filtered element
 * @returns [arrayFiltered, arrayOfElementsFiltered]
 */
export const filterAndReturnFilteredElements = <T>(array: T[], condition: (element: T) => boolean): [T[], T[]] => {
  return array.reduce(
    (acc: [T[], T[]], element) => {
      if (condition(element)) acc[0].push(element);
      else acc[1].push(element);
      return acc;
    },
    [[], []]
  );
};

export const choose = <T>(choices: T[]) => {
  const index = Math.floor(Math.random() * choices.length);
  return choices[index];
};

export const balanceAmountBetweenElements = <T>(elements: T[], amount: number): (T & { amount: number })[] => {
  let amountLeft = amount;
  const elementsWithBalancedAmount: (T & { amount: number })[] = [];
  elements.forEach((element, index) => {
    if (index === elements.length - 1) {
      elementsWithBalancedAmount.push({ ...element, amount: amountLeft });
    } else {
      const balancedAmount = roundAtSecondDecimal(Math.random() * amountLeft);
      elementsWithBalancedAmount.push({ ...element, amount: balancedAmount });
      amountLeft -= balancedAmount;
    }
  });
  return elementsWithBalancedAmount;
};

export const removeIndexFromArray = <T>(index: number, array: T[]) =>
  array.reduce((acc, currentElement, currentIndex) => {
    if (index !== currentIndex) {
      acc.push(currentElement);
    }
    return acc;
  }, [] as T[]);

export const doesArrayIncludeElements = <T>(array: T[], filter: { allElementsOf: T[] } | { atLeastOneOf: T[] }) => {
  if (filter.hasOwnProperty('allElementsOf')) {
    return (filter as { allElementsOf: T[] }).allElementsOf.every((element) => array.includes(element));
  }
  return (filter as { atLeastOneOf: T[] }).atLeastOneOf.some((element) => array.includes(element));
};

export const doesArrayContainsOtherElements = <T>(array: T[], elements: T[]) => {
  return array.filter((arrayElement) => !elements.includes(arrayElement)).length > 0;
};

export const isArrayFullOfEmptyElements = <T>(array: T[]) => {
  return array.every((element) => isNilOrEmpty(element));
};

/**
 * @param arr Array to look into
 * @param field The field to look (can contains . if inside a subobject)
 * @param object The object to check
 */
export const arrayIncludesFieldOfObject = <T>(arr: unknown[], field: keyof T | string, object: T): boolean => {
  if (isNil(object)) return false;
  let valueInSubObject = object[field as keyof T];

  const fields = (field as string).split('.');
  if (typeof fields === 'undefined' || fields.length <= 1) {
    return arr.includes(valueInSubObject);
  }
  valueInSubObject = object[fields[0] as keyof T];
  if (typeof valueInSubObject !== 'undefined' && Array.isArray(valueInSubObject)) {
    if (isEmpty(valueInSubObject)) {
      return false;
    }
    // For each element of this new array, I need to check if it is included in arr
    const result = valueInSubObject
      .map((v) => arrayIncludesFieldOfObject(arr, fields.slice(1).join('.'), v))
      .reduce((currentValue, value) => {
        return currentValue && value;
      }, true);
    return result;
  }
  return arrayIncludesFieldOfObject(arr, fields.slice(1).join('.'), valueInSubObject);
};

export const hasArrayChanged = (a: string[] | null | undefined, b: string[] | null | undefined): boolean =>
  !isEqual([...(a ?? [])].sort(), [...(b ?? [])].sort());

export const mergeObjectInArrayById = <T extends Record<string, unknown>>(items: T[], key: keyof T): T[] => {
  const merged: Record<string, T> = {};

  for (const item of items) {
    const keyValue = String(item[key]);
    const existing = merged[keyValue];
    merged[keyValue] = existing ? { ...existing, ...item } : item;
  }

  return Object.values(merged);
};

export const toggleInSet = <T>(set: Set<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

/** A copy of the list with the item of the same id replaced, or the item appended when the list has none. */
export const upsertById = <T extends ObjectWithId>(items: T[], item: T): T[] =>
  items.some(({ id }) => id === item.id)
    ? items.map((current) => (current.id === item.id ? item : current))
    : [...items, item];
